import { Download, Upload } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { Button, Card, CardHeader, Textarea } from '../../../components/ui';
import { useT } from '../../../i18n';
import { useImportAccounts, useImportRegistration } from './api';
import {
  importTemplate,
  parseImport,
  parseRegistrationImport,
  saveTemplate,
  type ImportParse,
} from './format';
import { Fail, Notice } from './shared';

/**
 * Lists pasted or loaded from a CSV: into the pool («phone; IIN; BIN; label»)
 * and into «for registration» («kind; phone; IIN; BIN; label»). The file is
 * read in the browser; only the parsed rows go to TestRun.
 */

const FileButton = ({ onText }: { onText: (text: string) => void }) => {
  const { t } = useT();
  const id = useId();
  const read = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onText(String(reader.result ?? ''));
    reader.readAsText(file);
  };
  return (
    <label
      htmlFor={id}
      className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-ink-muted transition-colors hover:bg-sunken hover:text-ink"
    >
      <Upload className="size-3.5" aria-hidden="true" />
      {t('automation.accounts.import.file')}
      <input
        id={id}
        type="file"
        accept=".csv,.txt,text/csv,text/plain"
        className="sr-only"
        onChange={(e) => read(e.target.files?.[0])}
      />
    </label>
  );
};

const BadLines = ({ bad }: { bad: ImportParse<unknown>['bad'] }) => {
  const { t } = useT();
  if (!bad.length) return null;
  return (
    <ul className="space-y-0.5">
      {bad.slice(0, 8).map((b) => (
        <li key={b.line} className="font-mono text-[12px] text-ink-muted">
          {t('automation.accounts.import.badLine', {
            line: b.line,
            reason: t(`automation.accounts.import.problem.${b.reason}`),
            text: b.text,
          })}
        </li>
      ))}
      {bad.length > 8 ? (
        <li className="text-[12px] text-ink-muted">
          {t('automation.accounts.import.more', { count: bad.length - 8 })}
        </li>
      ) : null}
    </ul>
  );
};

const ImportForm = ({
  text,
  onText,
  placeholder,
  label,
  counts,
  submit,
  onClose,
  extraActions,
}: {
  text: string;
  onText: (text: string) => void;
  placeholder: string;
  label: string;
  counts: { rows: number; bad: number };
  submit: ReactNode;
  onClose: () => void;
  extraActions?: ReactNode;
}) => {
  const { t } = useT();
  return (
    <>
      <div className="flex flex-wrap justify-end gap-1">
        {extraActions}
        <FileButton onText={onText} />
      </div>
      <Textarea
        rows={6}
        aria-label={label}
        className="font-mono text-[12px]"
        value={text}
        onChange={(e) => onText(e.target.value)}
        placeholder={placeholder}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[13px] text-ink-muted">
          {t('automation.accounts.import.toImport', { count: counts.rows })}
          {counts.bad
            ? `, ${t('automation.accounts.import.withError', { count: counts.bad })}`
            : ''}
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
          {submit}
        </div>
      </div>
    </>
  );
};

/** Into the pool: accounts appear at once, TestRun checks them one by one in the background. */
export const PoolImportPanel = ({ onClose }: { onClose: () => void }) => {
  const { t } = useT();
  const [text, setText] = useState('');
  const { rows, bad } = parseImport(text);
  const imp = useImportAccounts();

  const template = () =>
    saveTemplate(
      importTemplate({
        comments: [
          t('automation.accounts.import.templateLine1'),
          t('automation.accounts.import.templateLine2'),
        ],
        header: t('automation.accounts.import.templateHeader'),
        labels: [
          t('automation.accounts.import.templateLabel1'),
          t('automation.accounts.import.templateLabel2'),
        ],
      }),
      t('automation.accounts.import.templateFile'),
    );

  return (
    <Card>
      <CardHeader
        title={t('automation.accounts.import.poolTitle')}
        description={t('automation.accounts.import.poolHint')}
      />
      <div className="space-y-3 px-4 py-3">
        <ImportForm
          text={text}
          onText={setText}
          label={t('automation.accounts.import.poolTitle')}
          placeholder={t('automation.accounts.import.poolPlaceholder')}
          counts={{ rows: rows.length, bad: bad.length }}
          onClose={onClose}
          extraActions={
            <Button size="sm" variant="ghost" onClick={template}>
              <Download className="size-3.5" aria-hidden="true" />
              {t('automation.accounts.import.template')}
            </Button>
          }
          submit={
            <Button
              size="sm"
              variant="primary"
              onClick={() => imp.mutate(rows)}
              disabled={!rows.length}
              loading={imp.isPending}
            >
              {imp.isPending ? null : <Upload className="size-3.5" aria-hidden="true" />}
              {t('automation.accounts.import.addAndCheck')}
            </Button>
          }
        />
        <BadLines bad={bad} />
        {imp.isError ? (
          <Fail title={t('automation.accounts.import.failed')} error={imp.error} />
        ) : null}
        {imp.data ? (
          <Notice
            tone="pass"
            title={[
              t('automation.accounts.import.poolDone', {
                added: imp.data.added,
                updated: imp.data.updated,
              }),
              imp.data.invalid.length
                ? t('automation.accounts.import.rejected', { count: imp.data.invalid.length })
                : null,
            ]
              .filter(Boolean)
              .join(', ')}
          >
            {imp.data.checkStarted
              ? t('automation.accounts.import.checkStarted')
              : t('automation.accounts.import.checkWasRunning')}
          </Notice>
        ) : null}
      </div>
    </Card>
  );
};

/** Into «for registration»: unregistered clients; those already in the pool are skipped. */
export const RegistrationImportPanel = ({ onClose }: { onClose: () => void }) => {
  const { t } = useT();
  const [text, setText] = useState('');
  const { rows, bad } = parseRegistrationImport(text);
  const imp = useImportRegistration();
  return (
    <div className="space-y-3 rounded-lg border border-dashed border-border p-3">
      <p className="text-[12px] text-ink-muted">{t('automation.accounts.import.regHint')}</p>
      <ImportForm
        text={text}
        onText={setText}
        label={t('automation.accounts.import.regTitle')}
        placeholder={t('automation.accounts.import.regPlaceholder')}
        counts={{ rows: rows.length, bad: bad.length }}
        onClose={onClose}
        submit={
          <Button
            size="sm"
            variant="primary"
            onClick={() => imp.mutate(rows)}
            disabled={!rows.length}
            loading={imp.isPending}
          >
            {imp.isPending ? null : <Upload className="size-3.5" aria-hidden="true" />}
            {t('automation.accounts.import.add')}
          </Button>
        }
      />
      <BadLines bad={bad} />
      {imp.isError ? (
        <Fail title={t('automation.accounts.import.failed')} error={imp.error} />
      ) : null}
      {imp.data ? (
        <Notice
          tone="pass"
          title={[
            t('automation.accounts.import.regDone', {
              added: imp.data.added,
              updated: imp.data.updated,
            }),
            imp.data.pooled
              ? t('automation.accounts.import.pooled', { count: imp.data.pooled })
              : null,
            imp.data.invalid.length
              ? t('automation.accounts.import.rejected', { count: imp.data.invalid.length })
              : null,
          ]
            .filter(Boolean)
            .join(', ')}
        />
      ) : null}
    </div>
  );
};
